import React, { useMemo } from "react";
import { styled, Typography, Stack, Card, Collapse, IconButton, CardActionArea, useTheme, Link } from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";

import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import moment from "moment";
import { GetFrameRate, ReactPlayerMarker } from "../../../Redux/Reducers/redux-slice-react-player";
import { JWPMarkerTimecode } from "../../../Utils/Timecode/ReactPlayer";
import useBasketActions from "../../../Utils/Hooks/useBasketActions";
import { ModalContext } from "../../Modals";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../Modals/ModalConfirmAction/modal-confirm-action";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { ClipContext } from "../../../Core/Api/Types/clip-context";
import { PlayingStatusByTimecode } from "../../MediaPlayer/ReactPlayerWrapper/playing-statuses";
import { UserHasAcl } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { GetMetadataByTagName } from "../../../Core/Utils/asset-object-utils";
import { ClipDTO } from "../../../Pages/ViewLink/view-link";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import { useTranslation } from "react-i18next";
import { ChevronRightOutlined, TimerOutlined } from "@mui/icons-material";

export type ClipCardProps = {
  className?: string;
  id: number;
  clipName?: string | null;
  clipDescription?: string | null;
  timecodes: {
    in: ReactPlayerMarker;
    out: ReactPlayerMarker;
  };
  timecodeIn?: string;
  timecodeOut?: string;
  createdBy?: string | null;
  createdAt?: string | null;
  isPlaying?: boolean;
  isExpanded?: boolean;
  isSelected?: boolean;
  onClickMoreInfos?: () => void;
  onClickPlay?: () => void;
  onClickExpand?: () => void;
  onClickUpload?: () => void;
  onSelect?: () => void;
  expandable?: boolean;
  playable?: boolean;
  selectable?: boolean;
  contentOnly?: boolean;
  context?: ClipContext;
  importedOnAssetId?: boolean;
  deleteCallback?: () => void;
  specificTimecodeInteraction?: (x: number) => void;
};

const ClipCard: React.FC<ClipCardProps> = (props) => {
  const { t } = useTranslation();
  const customSnackbar = useCustomSnackbar();
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const { addObjectByIdToBasket } = useBasketActions();
  const [isPlayingByTimecode, SetIsPlayingByTimecode] = React.useState<boolean>(false);
  const frameRate = useAppSelector(GetFrameRate);
  const theme = useTheme();
  const aclClipDelete = UserHasAcl(AclTypes.aclClipDelete);
  const {
    id,
    clipName,
    clipDescription,
    timecodes,
    createdBy,
    createdAt,
    importedOnAssetId,
    isExpanded = false,
    isSelected = false,
    expandable = false,
    playable = false,
    selectable = false,
    contentOnly = false,
    context = ClipContext.Asset,
    isPlaying,
    onClickMoreInfos,
    onClickPlay,
    onClickExpand,
    onClickUpload,
    onSelect,
    deleteCallback,
    specificTimecodeInteraction: SpecifiqueTimecodeInteraction,
    timecodeIn,
    timecodeOut,
    ...otherProps
  } = props;

  const [hover, setHover] = React.useState(false);
  const dispatch = useAppDispatch();

  const _onClickPlay: React.MouseEventHandler<HTMLDivElement> = React.useCallback(
    (e) => {
      e.stopPropagation();
      if (e !== undefined) e.stopPropagation();
      if (onClickPlay) return onClickPlay();
    },
    [onClickPlay]
  );

  const HandleDeleteClipConfirm = (id: number) => {
    if (deleteCallback === undefined) return;
    deleteCallback();
  };

  const finalFr = frameRate && frameRate !== 0 ? frameRate : 25;

  const clipDuration = useMemo(
    () => ((timecodes.out.markFrame - timecodes.in.markFrame) / finalFr).toFixed(0),
    [timecodes, finalFr]
  );

  const _renderContent = () => (
    <CardActionArea
      component="div"
      sx={{ height: "100%", borderRadius: "4px" }}
      onMouseEnter={(e) => setHover(true)}
      onMouseLeave={(e) => setHover(false)}
    >
      <Stack direction="row" alignItems="center">
        {playable && (
          <PlayingStatusByTimecode
            fontSize="small"
            isplaying={isPlaying}
            SetIsPlayingByTimecode={SetIsPlayingByTimecode}
            timecode={timecodes}
            _onClickPlay={onClickPlay !== undefined ? _onClickPlay : undefined}
          />
        )}
        <Stack mr="auto">
          <Typography mb={0.5} fontWeight={500} color={isPlayingByTimecode ? "primary.text" : "text.primary"}>
            {clipName}
          </Typography>

          <Stack direction="row" alignItems="center">
            <JWPMarkerTimecode
              frameRate={finalFr}
              {...timecodes.in}
              runOnclick={SpecifiqueTimecodeInteraction}
            ></JWPMarkerTimecode>
            <ChevronRightOutlined fontSize="small" sx={(t) => ({ color: theme.palette.text.tier })} />
            <JWPMarkerTimecode
              frameRate={finalFr}
              {...timecodes.out}
              runOnclick={SpecifiqueTimecodeInteraction}
            ></JWPMarkerTimecode>
            <Stack sx={{ ml: 1 }} direction="row" spacing={0.5}>
              <TimerOutlined fontSize="small" sx={(t) => ({ color: theme.palette.text.tier })} />
              <Typography variant="body1" color="text.secondary">
                {clipDuration}s
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <Collapse in={hover}>
        <Stack pt={1} pl={5.5} pr={2} pb={1} spacing={2}>
          {(createdBy || createdAt || clipDescription) && (
            <>
              {clipDescription && (
                <Typography variant="body1" color="text.secondary">
                  {clipDescription}
                </Typography>
              )}
              <Stack gap={1}>
                {createdBy && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography color="text.tier">{t("common:createdBy")}:</Typography>
                    <Typography color="text.secondary">{createdBy}</Typography>
                  </Stack>
                )}
                {createdAt && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography color="text.tier">{t("common:createdAtLowerCase")}:</Typography>
                    <Typography color="text.secondary">
                      {moment(createdAt).format(GlobalConst.MOMENT_DATE_FORMAT_FULL)}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </>
          )}
          <Stack direction="row" justifyContent={"space-between"} alignItems={"center"}>
            <Link color={theme.palette.text.primary} underline="hover" onClick={onClickMoreInfos}>
              {t("button:viewMore")}
            </Link>
            <Stack direction={"row"} spacing={0.5}>
              {context !== ClipContext.Share && (
                <IconButton
                  color="neutral"
                  size="small"
                  onClick={() => {
                    addObjectByIdToBasket(id);
                  }}
                >
                  <ShoppingBagOutlinedIcon />
                </IconButton>
              )}
              {deleteCallback && (
                <IconButton
                  color="error"
                  size="small"
                  disabled={!aclClipDelete}
                  onClick={() => {
                    if (!openModal) return;

                    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
                      displayComment: false,
                      commentLabel: t("modal:message.confirmDeleteClip"),
                      title: t("modal:title.deleteThisClip"),
                      onConfirm: () => HandleDeleteClipConfirm(id),
                    });
                  }}
                >
                  <DeleteOutlinedIcon fontSize="small" sx={{ color: "error.main" }} />
                </IconButton>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Collapse>
    </CardActionArea>
  );

  return contentOnly ? (
    _renderContent()
  ) : (
    <StyledClipCard
      variant="outlined"
      expandable={expandable}
      selectable={selectable}
      playable={playable}
      isExpanded={isExpanded}
      aria-selected={isSelected}
      {...otherProps}
    >
      {_renderContent()}
    </StyledClipCard>
  );
};

export const StyledClipCard = styled(Card, {
  shouldForwardProp: (prop) =>
    prop !== "expandable" && prop !== "playable" && prop !== "isExpanded" && prop !== "selectable",
})<Pick<ClipCardProps, "isExpanded" | "expandable" | "playable" | "selectable">>(
  ({ theme, isExpanded, playable, expandable, selectable }) => ({
    ...(isExpanded && { backgroundColor: theme.palette.backgroundAlpha.pressed }),
    overflow: "visible",
    ".MuiCardActionArea-root": {
      transition: ".3s background, .3s border-color",
      "&:hover": {
        background: theme.palette.backgroundAlpha.hover,
        borderColor: theme.palette.backgroundAlpha.borderFocus,
      },
      padding: `${theme.spacing(1)} ${theme.spacing(expandable ? 0.5 : 1)} ${theme.spacing(1)} ${theme.spacing(
        playable ? 0.5 : 1
      )}`,
      ...(!selectable && {
        ".MuiCardActionArea-focusHighlight, .MuiTouchRipple-root": {
          display: "none",
        },
      }),
    },
  })
);

export default ClipCard;

export const ConvertAssetClipToClipCard = (y, frameRate): ClipCardProps => {
  var metadataMarker = GetMetadataByTagName(y, "clipInfo");
  return {
    id: y.id,
    clipName: y.name,
    clipDescription: y.comment,
    timecodes: {
      in: {
        markFrame: metadataMarker?.value.frameIn,
        isSet: true,
      },
      out: {
        markFrame: metadataMarker?.value.frameOut,
        isSet: true,
      },
    },
    createdAt: new Date(metadataMarker?.creationTime!).toDateString(),
    createdBy: y.creationInfos?.userName,
  };
};

export const ConvertShareClipToClipCard = (y: ClipDTO): ClipCardProps => {
  return {
    id: y.id,
    clipName: "",
    clipDescription: y.comment,
    timecodes: {
      in: {
        markFrame: y.timecodes.in.markFrame,
        isSet: y.timecodes.in.isSet,
      },
      out: {
        markFrame: y.timecodes.out.markFrame,
        isSet: y.timecodes.out.isSet,
      },
    },
    createdAt: y.date!.toString(),
    createdBy: y.userName,
  };
};
